import React, { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { motion, AnimatePresence } from "motion/react"
import ConfirmModal, { ConfirmModalHandle } from "../../components/ConfirmModal"
import { useRequest } from "../../hooks/useRequest"
import { appStore } from "../../store/appStore"
import { City } from "../../types/entities/city"
import { Cinema } from "../../types/entities/cinema"
import { cinemaDisplayName } from "../../lib/cinemaDisplayName"
import { isCityChange, selectCinemaLocation } from "../../lib/locationSwitch"
import { suggestNearestCity } from "../../lib/locationResolve"
import { isSearchBot } from "../../lib/isSearchBot"
import { cinemaHref, slugForCinemaId } from "../../lib/cinemaSlugs"

const DISPLAY = "'Tilda Sans', sans-serif"
const BODY = "'Manrope', sans-serif"
const MONO = "'JetBrains Mono', monospace"
const ACCENT_COLORS = ["#e4206b", "#349be6", "#29cc6a"]

function Sticker({ label, color, rotate = 0 }: { label: string; color: string; rotate?: number }) {
    return (
        <span
            className="inline-flex items-center px-3 py-1 rounded-full border text-[11px] font-bold uppercase tracking-widest whitespace-nowrap select-none"
            style={{
                fontFamily: MONO,
                color,
                borderColor: `${color}66`,
                background: `${color}22`,
                transform: `rotate(${rotate}deg)`,
            }}>
            {label}
        </span>
    )
}

function CinemaSkeleton() {
    return (
        <div className="rounded-[24px] border border-white/10 bg-white/[0.04] p-6 animate-pulse">
            <div className="h-10 bg-white/10 rounded-xl mb-4 w-3/4" />
            <div className="h-4 bg-white/10 rounded mb-2 w-full" />
            <div className="h-4 bg-white/10 rounded mb-4 w-1/2" />
            <div className="h-9 bg-white/10 rounded-full w-full mt-4" />
        </div>
    )
}

export type CinemaPickerProps = {
    sectionRef?: React.RefObject<HTMLDivElement | null>
    variant?: "embedded" | "modal"
    onApplied?: () => void
}

export default function CinemaPicker({ sectionRef, variant = "embedded", onApplied }: CinemaPickerProps) {
    const navigate = useNavigate()
    const { city: storedCity, locationSelected } = appStore()
    const { requestData } = useRequest()

    const confirmRef = useRef<ConfirmModalHandle>(null)

    const [cities, setCities] = useState<City[]>([])
    const [cinemas, setCinemas] = useState<Cinema[]>([])
    const [activeCity, setActiveCity] = useState<City | null>(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        const load = async () => {
            setLoading(true)
            try {
                const [cityRes, cinemaRes] = await Promise.all([
                    requestData<City[]>({ type: "GET", path: "city", disableError: true }),
                    requestData<Cinema[]>({ type: "GET", path: "cinema", disableError: true }),
                ])
                if (cityRes.status === "success" && cityRes.data) {
                    setCities(cityRes.data)
                    const stored =
                        locationSelected && cityRes.data.find(c => c.id === storedCity.id)
                            ? cityRes.data.find(c => c.id === storedCity.id)!
                            : null
                    if (stored) {
                        setActiveCity(stored)
                    } else if (!locationSelected) {
                        const suggested = await suggestNearestCity(requestData).catch(() => null)
                        // Пользователь без гео сам тыкает город. Роботу нужен открытый список залов.
                        setActiveCity(suggested ?? (isSearchBot() ? cityRes.data[0] ?? null : null))
                    } else {
                        setActiveCity(cityRes.data[0] ?? null)
                    }
                }
                if (cinemaRes.status === "success" && cinemaRes.data) {
                    setCinemas(cinemaRes.data)
                }
            } finally {
                setLoading(false)
            }
        }
        load().catch(console.error)
    }, [])

    const cityCinemas = activeCity ? cinemas.filter(c => c.city_id === activeCity.id) : []

    const handleCityClick = (city: City) => {
        setActiveCity(city)
    }

    const applyCinemaSelection = (cinema: Cinema) => {
        if (!activeCity) return
        selectCinemaLocation({
            city: activeCity,
            cinema,
            onApplied:
                onApplied ??
                (() => {
                    const slug = slugForCinemaId(cinema.id)
                    navigate(slug ? cinemaHref(slug, "/afisha") : "/afisha")
                }),
        })
    }

    const handleCinemaClick = (cinema: Cinema) => {
        if (!activeCity) return

        if (isCityChange(storedCity.id, activeCity.id)) {
            confirmRef.current?.open({
                title: "Смена города",
                description: "После смены города потребуется повторная авторизация. Вы уверены, что хотите сменить город?",
                actions: [
                    {
                        label: "Сменить город",
                        variant: "primary",
                        onClick: () => applyCinemaSelection(cinema),
                    },
                    {
                        label: "Отмена",
                        variant: "secondary",
                        onClick: () => {},
                    },
                ],
            })
            return
        }

        applyCinemaSelection(cinema)
    }

    const isModal = variant === "modal"

    return (
        <>
            <ConfirmModal ref={confirmRef} />
            <div
                ref={sectionRef ?? undefined}
                className={isModal ? "w-full" : "max-w-[1240px] mx-auto px-4 sm:px-8 w-full py-16 sm:py-20"}>
            <div className={`flex items-end justify-between flex-wrap gap-4 ${isModal ? "mb-6" : "mb-10"}`}>
                <h2
                    className="text-white"
                    style={{
                        fontFamily: DISPLAY,
                        fontWeight: 800,
                        fontSize: isModal ? "clamp(22px, 4vw, 36px)" : "clamp(28px, 5vw, 52px)",
                        letterSpacing: "-0.02em",
                    }}>
                    Выбери кинотеатр
                </h2>
                {activeCity && <Sticker label={`📍 ${activeCity.name}`} color="#e4206b" rotate={2} />}
            </div>

            <div className="flex flex-col lg:flex-row gap-8 items-start">
                <div className="flex flex-row flex-wrap lg:flex-col gap-2.5 shrink-0 lg:w-[200px]">
                    {loading
                        ? Array.from({ length: 4 }).map((_, i) => (
                              <div key={i} className="h-10 w-24 rounded-full bg-white/10 animate-pulse" />
                          ))
                        : cities.map(city => {
                              const on = activeCity?.id === city.id
                              return (
                                  <motion.button
                                      key={city.id}
                                      whileTap={{ scale: 0.95 }}
                                      onClick={() => handleCityClick(city)}
                                      className="rounded-full border px-5 py-2.5 text-[12px] uppercase tracking-[0.05em] transition-colors text-left"
                                      style={{
                                          fontFamily: MONO,
                                          fontWeight: 700,
                                          background: on ? "#349be6" : "rgba(255,255,255,0.06)",
                                          borderColor: on ? "#349be6" : "rgba(255,255,255,0.15)",
                                          color: on ? "#fff" : "rgba(255,255,255,0.7)",
                                          boxShadow: on ? "0 0 14px rgba(52,155,230,0.4)" : "none",
                                      }}>
                                      {city.name}
                                  </motion.button>
                              )
                          })}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 flex-1 w-full">
                    <AnimatePresence mode="wait">
                        {loading ? (
                            <>
                                <CinemaSkeleton />
                                <CinemaSkeleton />
                                <CinemaSkeleton />
                            </>
                        ) : cityCinemas.length === 0 ? (
                            <motion.div
                                key="empty"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="col-span-full flex flex-col items-center py-16 text-white/30 gap-3">
                                <span className="text-5xl">🎬</span>
                                <p style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 18 }}>
                                    Нет кинотеатров в этом городе
                                </p>
                            </motion.div>
                        ) : (
                            cityCinemas.map((cinema, i) => {
                                const accent = ACCENT_COLORS[i % 3]
                                return (
                                    <motion.div
                                        key={cinema.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.3, delay: i * 0.06 }}
                                        whileHover={{ y: -6 }}
                                        onClick={() => handleCinemaClick(cinema)}
                                        className="group relative flex flex-col gap-4 rounded-[24px] border border-white/10 bg-white/[0.05] p-6 cursor-pointer overflow-hidden">
                                        <div
                                            className="absolute -top-10 -right-10 size-32 rounded-full blur-2xl opacity-0 group-hover:opacity-30 transition-opacity duration-500 pointer-events-none"
                                            style={{ background: accent }}
                                        />

                                        {cinema.logo && (
                                            <img
                                                src={cinema.logo}
                                                alt={cinema.name}
                                                className="h-10 w-auto object-contain object-left"
                                            />
                                        )}

                                        <div>
                                            <h3
                                                className="text-white leading-tight"
                                                style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 16 }}>
                                                {cinemaDisplayName(cinema)}
                                            </h3>
                                            {cinema.address && (
                                                <p
                                                    className="mt-1 text-[13px] leading-snug"
                                                    style={{
                                                        fontFamily: BODY,
                                                        fontWeight: 500,
                                                        color: "rgba(255,255,255,0.55)",
                                                    }}>
                                                    {cinema.address}
                                                </p>
                                            )}
                                        </div>

                                        <div className="mt-auto">
                                            <span
                                                className="text-[11px] uppercase tracking-[0.1em]"
                                                style={{ fontFamily: MONO, fontWeight: 700, color: accent }}>
                                                выбрать →
                                            </span>
                                        </div>
                                    </motion.div>
                                )
                            })
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </div>
        </>
    )
}
